import {Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, inject} from '@angular/core';

export interface TrackItemReadEvent {
  itemId: number;
  itemTitle: string;
}

@Directive({
  selector: '[appTrackItemVisibility]',
  standalone: true
})
export class TrackItemVisibilityDirective implements OnInit, OnDestroy {
  private el: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);

  @Input({required: true}) public itemId!: number;
  @Input() public itemTitle: string = '';
  @Input() public trackingScope: string = 'default';
  @Input() public trackEnabled: boolean = true;
  @Input() public minVisibleRatio: number = 0.5;
  @Input() public dwellMs: number = 1000;

  @Output() public itemRead: EventEmitter<TrackItemReadEvent> = new EventEmitter<TrackItemReadEvent>();

  private observer?: IntersectionObserver;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private trackedRead: boolean = false;

  private static readonly seenReads: Set<string> = new Set<string>();

  public ngOnInit(): void {
    if (!this.trackEnabled || this.invalidItemId() || TrackItemVisibilityDirective.seenReads.has(this.readKey())) {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const visibleEnough = entry.isIntersecting && entry.intersectionRatio >= this.minVisibleRatio;
          if (visibleEnough) {
            this.startReadTimer();
          } else {
            this.clearTimer();
          }
        }
      },
      {threshold: [0, this.minVisibleRatio, 1]}
    );

    this.observer.observe(this.el.nativeElement);
  }

  public ngOnDestroy(): void {
    this.clearTimer();
    this.observer?.disconnect();
  }

  private startReadTimer(): void {
    if (this.trackedRead || this.timer || TrackItemVisibilityDirective.seenReads.has(this.readKey())) {
      return;
    }

    this.timer = setTimeout(() => {
      this.trackedRead = true;
      TrackItemVisibilityDirective.seenReads.add(this.readKey());
      this.itemRead.emit({itemId: this.itemId, itemTitle: this.itemTitle});
      this.clearTimer();
      this.observer?.disconnect();
    }, this.dwellMs);
  }

  private clearTimer(): void {
    if (!this.timer) {
      return;
    }

    clearTimeout(this.timer);
    this.timer = null;
  }

  private invalidItemId(): boolean {
    return !Number.isInteger(this.itemId) || this.itemId <= 0;
  }

  private readKey(): string {
    return `${this.trackingScope}:${this.itemId}`;
  }
}
